import {
  AnalyticsTrackingEvent,
  BuyBoxDialog,
  isProductEligibleForCheckout,
  ProductRewardSearchResult,
} from '@brandclub/common-ui';
import { useTheme } from '@mui/material';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { Suspense, useEffect, useRef, useState } from 'react';

import { useBrandAppConfigWithFallback, useDomainConfig } from '@/api/cache/appConfigQueryOptions';
import { useCartUpdates } from '@/api/cache/cartQueryOptions';
import { useCurrentBrandclubProduct } from '@/api/cache/dtcProductQueryOptions';
import { useCurrentProductContext } from '@/hooks/useCurrentProductContext';
import { useTrackActions } from '../../hooks/useTracking';
import { useBCRouting } from '../../providers/BCRoutingContextProvider';
import { HOST_DOMAIN } from '../../utils/domain';
import BagDrawer from '../BagButton/BagDrawer';
import { AddToBagButton } from './AddToBagButtonBase';
import { CatalogV1InpageAddToBagContent, getCatalogV1PriorityEligibleProduct } from './CatalogV1InpageAddToBagContent';
import { getProductsToListForBuyBox, StoreLocator } from './inpageAddToBagHelpers';
import { ScrollSliderContainer } from './InpageAddToBagStyles';
import { MoreRetailersInfo } from './MoreRetailersInfo';
import { useAutoUpdateCurrentDtcProducts } from './useAutoUpdateCurrentDtcProducts';

const CatalogV1InpageAddToBagButton = ({ mobile }: { mobile?: boolean }) => {
  const theme = useTheme();
  const [isScrollSliderOn, setIsScrollSliderOn] = useState(false);
  const [isBuyBoxOpen] = useState(false);
  const inPageButtonRef = useRef<HTMLDivElement | null>(null);
  const { mutate: updateCart } = useCartUpdates();

  const { treatments } = useSplitTreatments({
    names: ['brandclub_checkout_disabled_domain'],
    attributes: {
      domain: window.location.hostname,
      bcSplit: (window as any).bcSplit,
    },
  });

  const disableCheckoutByDomain = treatments?.brandclub_checkout_disabled_domain?.treatment === 'disable';

  useEffect(() => {
    const handleScroll = () => {
      if (inPageButtonRef.current) {
        const rect = inPageButtonRef.current.getBoundingClientRect();
        const distance = mobile
          ? theme.settings?.featureFlags?.scrollSliderDisplayDistanceMobile
          : theme.settings?.featureFlags?.scrollSliderDisplayDistance;
        const threshold = Number(distance) || 0;
        setIsScrollSliderOn(rect.top < -threshold);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // trigger once on mount to set initial state correctly
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const enableScrollSliderDisplay = mobile
    ? !!theme.settings?.featureFlags?.scrollSliderDisplayMobile
    : !!theme.settings?.featureFlags?.scrollSliderDisplay;
  const enableLocationMap = mobile
    ? !!theme.settings?.featureFlags?.storeLocatorMapMobile
    : !!theme.settings?.featureFlags?.storeLocatorMap;

  const bcRouting = useBCRouting();
  const [trackCheckoutBarAction] = useTrackActions();

  const { data: domainConfig } = useDomainConfig();
  const { data: brandAppConfig } = useBrandAppConfigWithFallback();
  const { data: currentBrandclubProduct } = useCurrentBrandclubProduct();
  const currentProductContext = useCurrentProductContext();
  const checkoutPriorityProduct = brandAppConfig
    ? getCatalogV1PriorityEligibleProduct(
        currentBrandclubProduct,
        domainConfig?.checkoutAllowedOfferType,
        brandAppConfig.retailerIds,
      )
    : undefined;
  const soldOutPlaceholderProduct =
    !currentBrandclubProduct && !currentProductContext.stacklineSku
      ? ({ retailPrice: 0 } as ProductRewardSearchResult)
      : undefined;
  const hasNoCheckoutOffer =
    !!soldOutPlaceholderProduct || (!!brandAppConfig && !!currentBrandclubProduct && !checkoutPriorityProduct);
  const priorityProduct = checkoutPriorityProduct ?? currentBrandclubProduct ?? soldOutPlaceholderProduct;

  useAutoUpdateCurrentDtcProducts(currentBrandclubProduct);

  useEffect(() => {
    if (!priorityProduct) {
      return;
    }
    console.log('[BC Checkout] Selected in-page priority product', {
      flow: 'catalog-v1',
      retailerDisplayName: priorityProduct.retailerDisplayName,
      retailerId: priorityProduct.retailerId,
      retailerSku: priorityProduct.retailerSku,
      stacklineSku: priorityProduct.stacklineSku,
    });
  }, [
    priorityProduct?.retailerDisplayName,
    priorityProduct?.retailerId,
    priorityProduct?.retailerSku,
    priorityProduct?.stacklineSku,
  ]);

  const isEligibleForCheckout =
    !!checkoutPriorityProduct &&
    isProductEligibleForCheckout(
      checkoutPriorityProduct as ProductRewardSearchResult,
      domainConfig?.checkoutAllowedOfferType,
    );

  // Used by BuyBoxDialog and LocationMap - BuyBoxDialog handles disable check via its disabledCheckout prop
  const handleBuyClick = (product: ProductRewardSearchResult) => {
    if (!product) {
      return;
    }

    const isCurrentProductEligibleForCheckout = isProductEligibleForCheckout(
      product,
      domainConfig?.checkoutAllowedOfferType,
    );

    if (isCurrentProductEligibleForCheckout && !disableCheckoutByDomain) {
      updateCart({
        action: 'add',
        items: [{ ...product, quantity: 1 }],
        retailerId: product.retailerId,
      });
      trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART, {
        stacklineSku: product.stacklineSku,
        retailerId: product.retailerId,
        retailerSku: product.retailerSku,
        retailPrice: product.retailPrice,
        quantity: 1,
        source: 'brandclub_dtc_checkout_bar',
      });
      BagDrawer?.show();
    } else {
      trackCheckoutBarAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
        stacklineSku: product.stacklineSku,
        retailerId: product.retailerId,
        retailerSku: product.retailerSku,
        retailPrice: product.retailPrice,
        source: 'brandclub_dtc_checkout_bar',
      });

      bcRouting.navigate(product.retailerProductPageUrl, '_blank');
    }
  };

  const handleNonEligibleClick = () => {
    if (!priorityProduct) return;
    trackCheckoutBarAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
      stacklineSku: priorityProduct.stacklineSku,
      retailerId: priorityProduct.retailerId,
      retailerSku: priorityProduct.retailerSku,
      retailPrice: priorityProduct.retailPrice,
      source: 'brandclub_dtc_checkout_bar',
    });
    trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
      stacklineSku: priorityProduct.stacklineSku,
      retailerId: priorityProduct.retailerId,
      retailerSku: priorityProduct.retailerSku,
      retailPrice: priorityProduct.retailPrice,
      organicType: 'openRetailerProductPage',
      source: 'brandclub_dtc_pdp_view_button',
    });
    bcRouting.navigate(priorityProduct.retailerProductPageUrl, '_blank');
  };

  const renderAddToBagButton = ({ hideRewards }: { hideRewards: boolean }) => (
    <AddToBagButton
      ref={inPageButtonRef}
      mobile={mobile}
      onAddToCart={() => {
        if (checkoutPriorityProduct) {
          updateCart({
            action: 'add',
            items: [{ ...checkoutPriorityProduct, quantity: 1 }],
            retailerId: checkoutPriorityProduct.retailerId,
          });
          trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART, {
            stacklineSku: checkoutPriorityProduct.stacklineSku,
            retailerId: checkoutPriorityProduct.retailerId,
            retailerSku: checkoutPriorityProduct.retailerSku,
            retailPrice: checkoutPriorityProduct.retailPrice,
            quantity: 1,
            source: 'brandclub_dtc_checkout_bar',
          });
          BagDrawer?.show();
        }
      }}
      onViewRetailer={handleNonEligibleClick}
      currentBrandclubProduct={priorityProduct as ProductRewardSearchResult}
      originalProduct={currentBrandclubProduct}
      isEligibleForCheckout={isEligibleForCheckout}
      disabled={hasNoCheckoutOffer}
      nonEligibleButtonText={hasNoCheckoutOffer ? 'Sold out' : undefined}
      hideRewards={hideRewards || hasNoCheckoutOffer}
    />
  );

  const renderRetailerInfo = ({ hideMatchedRetailOffers }: { hideMatchedRetailOffers: boolean }) => (
    <MoreRetailersInfo
      handleBuyBoxBuyClick={handleBuyClick}
      hideMatchedRetailOffers={hideMatchedRetailOffers}
      product={currentBrandclubProduct}
      checkoutRetailerIds={brandAppConfig?.retailerIds}
    />
  );

  const renderLocationMap = () =>
    enableLocationMap ? (
      <div style={{ marginTop: '30px' }} className="bc-location-map-container">
        <Suspense fallback={null}>
          <StoreLocator
            mobile={mobile}
            stacklineSku={currentBrandclubProduct?.stacklineSku}
            API_URL={HOST_DOMAIN}
            product={currentBrandclubProduct}
            checkoutRetailerIds={brandAppConfig?.retailerIds}
            onAddProductToBag={() => {
              const productsToList = getProductsToListForBuyBox(currentBrandclubProduct, brandAppConfig?.retailerIds);
              if (productsToList.length > 1) {
                BuyBoxDialog.show({
                  checkoutAllowedOfferType: domainConfig?.checkoutAllowedOfferType,
                  products: productsToList,
                  onClick: handleBuyClick,
                  onProductPageClick: (product) => {
                    trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
                      stacklineSku: product.stacklineSku,
                      retailerId: product.retailerId,
                      retailerSku: product.retailerSku,
                      retailPrice: product.retailPrice,
                      organicType: 'openRetailerProductPage',
                      source: 'brandclub_dtc_location_map',
                    });
                    window.open(product.retailerProductPageUrl, '_blank');
                  },
                });
                return;
              }

              const productToAdd = productsToList[0] ?? checkoutPriorityProduct;
              if (productToAdd) {
                handleBuyClick(productToAdd);
              }
            }}
          />
        </Suspense>
      </div>
    ) : null;

  return (
    <>
      <CatalogV1InpageAddToBagContent
        renderAddToBagButton={renderAddToBagButton}
        renderLocationMap={renderLocationMap}
        renderRetailerInfo={renderRetailerInfo}
      />
      {/* // if the buy box is open, we don't want to show the scroll slider */}
      {enableScrollSliderDisplay && !isBuyBoxOpen && currentBrandclubProduct ? (
        <ScrollSliderContainer className={isScrollSliderOn ? 'scroll-on' : 'scroll-off'}>
          <div className="scroll-slider-inner">
            <AddToBagButton
              mobile={mobile}
              onAddToCart={() => {
                if (checkoutPriorityProduct) {
                  updateCart({
                    action: 'add',
                    items: [{ ...checkoutPriorityProduct, quantity: 1 }],
                    retailerId: checkoutPriorityProduct.retailerId,
                  });
                  trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART, {
                    stacklineSku: checkoutPriorityProduct.stacklineSku,
                    retailerId: checkoutPriorityProduct.retailerId,
                    retailerSku: checkoutPriorityProduct.retailerSku,
                    retailPrice: checkoutPriorityProduct.retailPrice,
                    quantity: 1,
                    source: 'brandclub_dtc_checkout_bar',
                  });
                  BagDrawer?.show();
                }
              }}
              onViewRetailer={handleNonEligibleClick}
              currentBrandclubProduct={priorityProduct as ProductRewardSearchResult}
              originalProduct={currentBrandclubProduct}
              isEligibleForCheckout={isEligibleForCheckout}
              disabled={hasNoCheckoutOffer}
              nonEligibleButtonText={hasNoCheckoutOffer ? 'Sold out' : undefined}
              hideRewards={hasNoCheckoutOffer}
            />
          </div>
        </ScrollSliderContainer>
      ) : null}
    </>
  );
};

export default CatalogV1InpageAddToBagButton;
